import { useState, useRef, type FC, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import Icon, { type IconName } from '@/components/common/Icon.tsx'
import RecordPanel from '@/components/Sidebar/Scripts/SpeechDialog/RecordPanel.tsx'
import useSpeechDialogStore from './speech-dialog.store.ts'
import { SpeechContentType } from './types.ts'
import ContextMenu from '../common/ContextMenu.tsx'
import type { AudioFileSource } from '@/features/media-file/domain/models/media-file.ts'
import PlayButton from '../Sidebar/Scripts/PlayButton.tsx'
import { formatSecondsToMinutesSeconds } from '@/utils/formatters.ts'
import { getFormattedAudioDuration } from '@/utils/get-audio-duration.ts'
import { isUploadSource } from '@/features/media-file/domain/predicates/is-upload-source.ts'
import { isRecordingSource } from '@/features/media-file/domain/predicates/is-recording-source.ts'
import Spinner from '../common/Spinner.tsx'

const speechAudioTypes = ['record', 'library', 'upload'] as const
type SpeechAudioType = (typeof speechAudioTypes)[number]
type SpeechAudioOption = {
  type: SpeechAudioType
  textKey: string
  iconName: IconName
}

const MediaSourceOption: FC<{
  type: SpeechAudioType
  label: string
  iconName: IconName
  onClick: (type: SpeechAudioType) => void
}> = ({ type, label, iconName, onClick }) => {
  return (
    <button
      type="button"
      className="flex h-[72px] flex-1 flex-col items-center justify-center gap-1 rounded bg-white/[6%] text-xs font-semibold text-white hover:bg-white/10 focus:outline-none"
      onClick={() => onClick(type)}
    >
      <Icon name={iconName} />
      <span>{label}</span>
    </button>
  )
}

const AudioFileItem: FC<{
  audio: AudioFileSource
  selected: boolean
  onSelect: () => void
  onDelete: (e: React.MouseEvent<HTMLButtonElement>) => void
}> = ({ audio, selected, onSelect, onDelete }) => {
  const { uploadLocalFile } = useSpeechDialogStore()
  const [calculatedDuration, setCalculatedDuration] = useState<string>('0:00')
  const [isCalculatingDuration, setIsCalculatingDuration] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const name = audio.mediaFile?.name || audio.file?.name || ''
  const icon = { media: 'folderSolid', upload: 'upload', recording: 'speech' }[
    audio.kind
  ] as IconName
  const info = { media: 'Library', upload: 'Upload', recording: 'Recording' }[
    audio.kind
  ]
  const soundId = audio.mediaFile?.id || audio.file?.name || ''
  const duration = audio.mediaFile?.duration
    ? formatSecondsToMinutesSeconds(audio.mediaFile.duration)
    : calculatedDuration
  const playFile = audio.file

  useEffect(() => {
    // Calculate duration for File objects (upload and recording)
    if (isRecordingSource(audio) || isUploadSource(audio)) {
      if (!audio.mediaFile) {
        const fx = async (): Promise<void> => {
          setIsUploading(true)
          await uploadLocalFile(audio.id)
          setIsUploading(false)
        }
        void fx()
      }
      setIsCalculatingDuration(true)
      getFormattedAudioDuration(audio.file)
        .then(setCalculatedDuration)
        .catch(() => setCalculatedDuration('0:00'))
        .finally(() => setIsCalculatingDuration(false))
    }
  }, [audio, uploadLocalFile])

  const classes = [
    'relative flex w-full items-center gap-3 rounded border border-neutral-700 px-4 py-3',
    !selected ? 'cursor-pointer' : '',
  ].join(' ')

  return (
    <div className={classes} onClick={onSelect}>
      {/* Custom Radio */}
      {isUploading ? (
        <Spinner className="h-5 w-5" />
      ) : (
        <button
          type="button"
          className={`mr-1 flex h-5 w-5 items-center justify-center rounded-full border-2 focus:outline-none ${selected ? 'border-blue-500' : 'border-white'}`}
          aria-pressed={selected}
        >
          {selected && <div className="h-1.5 w-1.5 rounded-full bg-blue-500" />}
        </button>
      )}
      {/* File info */}
      <div className="flex flex-1 flex-col">
        <span className="text-sm font-medium text-white">{name}</span>
        <span className="flex items-center gap-2 text-xs font-semibold text-gray-400">
          {isCalculatingDuration ? (
            <div className="h-3 w-8 animate-pulse rounded bg-gray-600" />
          ) : (
            duration
          )}
          <Icon name="divider" width={4} height={4} fill="#a1a1aa" />
          <Icon name={icon} width={12} height={12} fill="#a1a1aa" />
          {info}
        </span>
      </div>
      {/* Actions */}
      <PlayButton
        soundId={soundId}
        file={playFile}
        audioSourceType={audio.kind === 'media' ? 'media-file' : audio.kind}
      />
      <button
        type="button"
        ref={menuButtonRef}
        className="flex h-9 w-9 items-center justify-center rounded-full hover:bg-white/10 focus:outline-none"
        onClick={(e) => {
          e.stopPropagation()
          setIsMenuOpen((open) => !open)
        }}
      >
        <Icon name="menuHorizontal" width={20} height={20} />
      </button>
      <ContextMenu
        viewModel={[
          {
            key: 'Remove',
            action: onDelete,
            icon: <Icon name="trash" width={18} height={18} />,
          },
        ]}
        isOpen={isMenuOpen}
        setIsOpen={setIsMenuOpen}
        position={{ right: 14, top: 44 }}
        triggerRef={menuButtonRef}
      />
    </div>
  )
}

const MediaSourceSelector: FC = () => {
  const { t } = useTranslation()
  const {
    setContentType,
    audioFiles,
    audioFilesError,
    setAudioFiles,
    addUploadAudioFile,
    addRecordingAudioFile,
    recordOpen,
    setRecordOpen,
    recordedAudio,
    setRecordedAudio,
    selectedAudioFileId,
    setSelectedAudioFileId,
  } = useSpeechDialogStore()
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const [audioFile] = audioFiles
    if (!selectedAudioFileId && audioFile) {
      setSelectedAudioFileId(audioFile.id)
    }
  }, [audioFiles, selectedAudioFileId, setSelectedAudioFileId])

  useEffect(() => {
    if (recordedAudio) {
      fetch(recordedAudio)
        .then((res) => res.blob())
        .then((blob) => {
          const wavBlob = new Blob([blob], { type: 'audio/wav' })
          const file = new File([wavBlob], `Recording-${Date.now()}.wav`, {
            type: 'audio/wav',
          })
          addRecordingAudioFile(file)
          setRecordedAudio(null)
        })
    }
  }, [recordedAudio, addRecordingAudioFile, setRecordedAudio])

  const speechAudioOptions = speechAudioTypes.map(
    (type): SpeechAudioOption => ({
      type,
      textKey: `ls_generic_add_speech_audio_${type}`,
      iconName:
        type === 'record'
          ? 'speech'
          : type === 'library'
            ? 'folderSolid'
            : 'upload',
    })
  )

  const audioOptionClick = (type: SpeechAudioType) => {
    const actions = {
      record: () => setRecordOpen(true),
      library: () => setContentType(SpeechContentType.Library),
      upload: () => fileInputRef.current?.click(),
    }
    actions[type]()
  }

  const uploadFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    // Process files in reverse order so they appear in the correct order at the top
    // (since each addUploadAudioFile call adds to the beginning of the array)
    Array.from(files)
      .reverse()
      .forEach((file) => {
        addUploadAudioFile(file)
      })

    // Clear the input so the same files can be selected again if needed
    e.target.value = ''
  }

  return (
    <div className="flex flex-col gap-3">
      {recordOpen ? (
        <RecordPanel />
      ) : (
        <>
          <div className="flex justify-between">
            <span className="text-sm font-medium text-white">
              {t('ls_generic_add_speech_audio')}
            </span>
            {audioFilesError && (
              <div className="flex items-center text-xs text-red-500">
                {t(audioFilesError)}
              </div>
            )}
          </div>
          <div className="flex gap-4">
            {speechAudioOptions.map(({ type, textKey, iconName }) => (
              <MediaSourceOption
                key={type}
                type={type}
                label={t(textKey)}
                iconName={iconName}
                onClick={audioOptionClick}
              />
            ))}
            <input
              ref={fileInputRef}
              type="file"
              accept="audio/*"
              multiple
              className="hidden"
              onChange={uploadFile}
            />
          </div>
          {/* {isGenerating && ( 
          <div className="flex items-center gap-2 py-2 text-blue-400">
            <Icon name="sparkles" />
            {t('ls_generic_generating')}
          </div>
           )} */}
          <div className="flex flex-col">
            {audioFiles.length > 0 && (
              <>
                <span className="py-3 text-xs font-semibold uppercase text-gray-400">
                  {t('ls_generic_audio_added')}
                </span>
                <div className="flex flex-col gap-2">
                  {audioFiles.map((audio) => (
                    <AudioFileItem
                      key={audio.id}
                      audio={audio}
                      selected={selectedAudioFileId === audio.id}
                      onSelect={() => setSelectedAudioFileId(audio.id)}
                      onDelete={(e: React.MouseEvent<HTMLButtonElement>) => {
                        e.stopPropagation()
                        setAudioFiles(audioFiles.filter((f) => f !== audio))
                        if (selectedAudioFileId === audio.id) {
                          const next = audioFiles.find((f) => f !== audio)
                          if (next) {
                            setSelectedAudioFileId(next.id)
                          } else {
                            setSelectedAudioFileId(undefined)
                          }
                        }
                      }}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default MediaSourceSelector
